import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { acceptAdminInvite } from '../api/admins';
import { ApiError } from '../api/client';
import { Button, Input } from '../components/ui';
import { saveTokens } from '../features/auth/authStorage';
import styles from './LoginPage.module.scss';

export function AcceptAdminInvitePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get('challenge') ?? '';

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      return acceptAdminInvite({
        challenge_id: challengeId,
        code: code.trim(),
        password,
      });
    },
    onSuccess: (tokens) => {
      saveTokens(tokens);
      // Force full reload so AuthProvider restores the new session from storage.
      window.location.assign('/');
    },
  });

  const isMissingChallenge = !challengeId;
  const errorMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error instanceof Error
        ? t('roles.acceptInviteError')
        : null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    if (!mutation.isPending) {
      mutation.mutate();
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.brandLabel}>White&amp;Flex</span>
          <h1 className={styles.title}>{t('roles.acceptInviteTitle')}</h1>
          <p className={styles.subtitle}>{t('roles.acceptInviteSubtitle')}</p>
        </div>

        {isMissingChallenge ? (
          <div className={styles.alert} role="alert">
            {t('roles.acceptInviteError')}
          </div>
        ) : null}

        {mutation.isError && !mismatch ? (
          <div className={styles.alert} role="alert">
            {errorMessage}
          </div>
        ) : null}

        {mismatch ? (
          <div className={styles.alert} role="alert">
            {t('roles.passwordMismatch')}
          </div>
        ) : null}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fields}>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              label={t('roles.acceptInviteCode')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={isMissingChallenge || mutation.isPending}
            />
            <Input
              id="password"
              type="password"
              label={t('roles.acceptInvitePassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={isMissingChallenge || mutation.isPending}
            />
            <Input
              id="confirm-password"
              type="password"
              label={t('roles.acceptInvitePasswordConfirm')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={isMissingChallenge || mutation.isPending}
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={isMissingChallenge || mutation.isPending}
          >
            {mutation.isPending ? t('roles.saving') : t('roles.acceptInviteSubmit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
